<template>
  <div class="col">
    <!-- Heading -->
    <h2 class="text-primary pb-4">{{ $t("Partners.experts") }}</h2>

    <!-- Alphabet row -->
    <div class="row pl-3">
      <a 
        class="cursorPointer mr-3 text-primary" 
        @click="filterByLetter('All')"
        :class="{ 'font-weight-bold text-secondary': !startsWithCharacter }">
        {{ $t("General.all") }}
      </a>
      <a 
        v-for="char in alphabet" 
        :key="char" 
        class="mr-3 cursorPointer text-primary"
        @click="filterByLetter(char)"
        :class="{ 'font-weight-bold text-secondary': startsWithCharacter === char }">
        {{ char }}
      </a>
    </div>

    <!-- Authors grid -->
    <div v-if="authors && authors.length" class="row">
      <div 
        v-for="(author, index) in authors" 
        :key="index" 
        class="text-center col-xxxl-2 col-xxl-3 col-lg-4 col-md-6 col-sm-6 col-12 p-sm-3 p-2">
          <single-expert-feature :expert="author" :settingsPerm="settingsPerm" />
      </div>
    </div>

    <!-- Sentinel div for infinite scroll -->
    <div ref="infiniteScrollObserver" class="w-100"></div>

    <!-- Loader / placeholder -->
    <div v-if="!endOfInfiniteScroll" class="d-flex justify-content-center mb-3 placeholder-animated-brightness mt-3">
      <!-- Desktop placeholder (≥1200px) -->
        <img 
        class="d-none d-xl-block w-100" 
        src="/wp-content/themes/understrap-child/conferences-app-vue/walkuere_conferences_vue_app/src/assets/Single_loading_image_with_header_of_Experts.svg" 
        alt="">

      <!-- Tablet placeholder (≥768px and <1200px) -->
      <img 
        class="d-none d-md-block d-xl-none w-100" 
        src="/wp-content/themes/understrap-child/conferences-app-vue/walkuere_conferences_vue_app/src/assets/Single loading image with header + 3 collums of Experts.svg" 
        alt="">
      <!-- Mobile placeholder (<768px) -->
      <img 
        class="d-block d-md-none w-100" 
        src="/wp-content/themes/understrap-child/conferences-app-vue/walkuere_conferences_vue_app/src/assets/Single loading image with header + 2 columms of Experts.svg" 
        alt="">
    </div>
  </div>
</template>

<script>
import { ApolloClient, InMemoryCache } from '@apollo/client/core'
import { provideApolloClient } from '@vue/apollo-composable'
import CONCORD_QUERIES from "../services/concord_queries.js"
import singleExpertFeature from './brand/singleExpertFeature.vue'

export default {
  name: 'expertsListingDisplay',
  props: {
    settingsPerm: Object
  },
  data() {
    return {
      authors: [],
      alphabet: "AÄBCDEFGHIJKLMNOÖPQRSTUÜVWXYZ".split(""),
      startsWithCharacter: null,
      page: 1,
      pagesize: 24,
      shallWeLoadMore: true,
      endOfInfiniteScroll: false,
      apolloClient: null,
      observer: null
    }
  },
  components: {
    singleExpertFeature
  },
  created() {
    const cache = new InMemoryCache()
    this.apolloClient = new ApolloClient({
      cache,
      uri: this.settingsPerm.concordBackendURL,
    })
    provideApolloClient(this.apolloClient);

    this.loadAuthors();

    if (this.settingsPerm?.isDevMioActive) {
      this.expertRouteSegment = 'experts';
    } else {
      this.expertRouteSegment = 'experten';
    }
  },
  mounted() {
    // Initialize IntersectionObserver after DOM is ready
    this.$nextTick(() => {
      this.initObserver();
    });
  },
  unmounted() {
    if (this.observer && this.$refs.infiniteScrollObserver) {
      this.observer.unobserve(this.$refs.infiniteScrollObserver);
    }
  },
  methods: {
    initObserver() {
      if (!this.$refs.infiniteScrollObserver) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry.isIntersecting && !this.endOfInfiniteScroll) {
            this.loadAuthors();
          }
        },
        {
          root: null,
          rootMargin: '200px',
          threshold: 0.1
        }
      );

      this.observer.observe(this.$refs.infiniteScrollObserver);
    },

    async loadAuthors() {
      if (!this.shallWeLoadMore) return;

      const queryToUse = this.startsWithCharacter
        ? CONCORD_QUERIES.AUTHORS_QUERY_WITH_STARTS_WITH
        : CONCORD_QUERIES.AUTHORS_QUERY;

      const variables = this.startsWithCharacter
        ? { startsWith: this.startsWithCharacter, page: this.page, pagesize: this.pagesize }
        : { page: this.page, pagesize: this.pagesize };

      try {
        const { data } = await this.apolloClient.query({ query: queryToUse, variables });

        if (data?.authors?.Authors?.length) {
          this.authors.push(...data.authors.Authors);

          // Reattach observer after DOM updates
          this.$nextTick(() => {
            if (this.observer && this.$refs.infiniteScrollObserver) {
              this.observer.unobserve(this.$refs.infiniteScrollObserver);
              this.observer.observe(this.$refs.infiniteScrollObserver);
            }
          });

          if (data.authors.Meta?.count < this.pagesize) {
            this.shallWeLoadMore = false;
            this.endOfInfiniteScroll = true;
          }
          this.page++;
        } else {
          this.shallWeLoadMore = false;
          this.endOfInfiniteScroll = true;
        }
      } catch (err) {
        console.error("Error loading authors:", err);
        this.shallWeLoadMore = false;
        this.endOfInfiniteScroll = true;
      }
    },

    filterByLetter(char) {
      this.startsWithCharacter = (char === "All") ? null : char;
      this.page = 1;
      this.authors = [];
      this.shallWeLoadMore = true;
      this.endOfInfiniteScroll = false;
      this.loadAuthors();
    }
  }
}
</script>

<style scoped>
.experts-single-image{
    min-height: 230px;
    max-height: 230px;
    width: 230px;
    object-fit: cover;
}
</style>
